// SEQ-180 phase 2, task P12: the dashboards data layer.
//
// Writes go through PostgREST with RLS, not through an RPC (plan decision
// D14): these tables hold layout, not money, and every write below carries an
// `.eq('id', ...)` filter. Authorisation is the four policies on
// `pnl_dashboards` (supabase/migrations/20260910000100_pnl_dashboards.sql),
// not anything this file checks; the owner/admin test here only decides
// whether to RENDER an edit control, so a non-owner never meets a button that
// would fail (plan section 7.1, D23).
//
// THE X.1 RULE: supabase-js resolves `{ data, error }` on a non-2xx response
// and never rejects, so every call below reads `error` explicitly and
// surfaces it as a toast. Nothing here reverts local state silently: a failed
// write leaves the list untouched and says why, which is the rule written
// down after project rate edits "saved" and reverted with no error at all.
//
// `pnl_dashboards` and `pnl_dashboard_widgets` are not in the generated
// Supabase types (they are new in this release), so each call takes the same
// narrow `as any` escape EditExpenseCategoriesModal.tsx already uses for
// `pnl_metric_registry`, never a blanket cast on the client.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useToast } from "../../components/ui/ToastModal";
import type { PnlWidgetConfig, PnlWidgetLayout } from "../../lib/pnl/pnlWidgetSchema";
import type { PnlCustomDatePreset } from "./dashboardRequestKeys";

export interface PnlDashboardRow {
  id: string;
  organizationId: string;
  ownerId: string;
  name: string;
  isShared: boolean;
  isDefault: boolean;
  datePresets: PnlCustomDatePreset[];
}

interface RawDashboardRow {
  id: string;
  organization_id: string;
  owner_id: string;
  name: string;
  is_shared: boolean;
  is_default: boolean;
  date_presets: unknown;
}

/** One widget row to insert, config plus its grid size and its place in the order. */
export interface PnlDashboardWidgetSeed {
  config: PnlWidgetConfig;
  layout: PnlWidgetLayout;
  position: number;
}

function isCustomDatePreset(value: unknown): value is PnlCustomDatePreset {
  if (typeof value !== "object" || value === null) return false;
  const p = value as Record<string, unknown>;
  return (
    typeof p.id === "string" &&
    typeof p.name === "string" &&
    typeof p.from === "string" &&
    typeof p.to === "string"
  );
}

/**
 * Decodes `date_presets` field by field rather than casting it. The column is
 * `jsonb` with only an "is an array" CHECK behind it, so a malformed entry is
 * a real possibility; a bad entry is dropped and the rest of the dashboard
 * still loads, which is better than a filter bar that throws.
 */
function decodeDatePresets(value: unknown): PnlCustomDatePreset[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isCustomDatePreset);
}

function decodeDashboard(row: RawDashboardRow): PnlDashboardRow {
  return {
    id: row.id,
    organizationId: row.organization_id,
    ownerId: row.owner_id,
    name: row.name,
    isShared: row.is_shared,
    isDefault: row.is_default,
    datePresets: decodeDatePresets(row.date_presets),
  };
}

const DASHBOARD_COLUMNS = "id, organization_id, owner_id, name, is_shared, is_default, date_presets";

export interface UsePnlDashboardsResult {
  dashboards: PnlDashboardRow[];
  loading: boolean;
  /** Set when the list itself could not be read. Individual write failures surface as toasts instead. */
  error: string | null;
  refresh: () => void;
  createDashboard: (name: string, widgets: PnlDashboardWidgetSeed[]) => Promise<PnlDashboardRow | null>;
  renameDashboard: (id: string, name: string) => Promise<boolean>;
  duplicateDashboard: (id: string, name: string) => Promise<PnlDashboardRow | null>;
  setDashboardShared: (id: string, isShared: boolean) => Promise<boolean>;
  setDatePresets: (id: string, presets: PnlCustomDatePreset[]) => Promise<boolean>;
  deleteDashboard: (id: string) => Promise<boolean>;
}

/* eslint-disable @typescript-eslint/no-explicit-any -- pnl_dashboards is not in the generated Supabase types yet (SEQ-180 phase 2) */
export function usePnlDashboards(organizationId: string | null, userId: string | null): UsePnlDashboardsResult {
  const { addToast } = useToast();
  const [dashboards, setDashboards] = useState<PnlDashboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  // No synchronous setState in this effect body: `loading` and `dashboards`
  // are derived below for the no-organisation case, and the spinner is armed
  // by `refresh` (an event handler) rather than by the effect, which is what
  // keeps this off the cascading-render path.
  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;

    (supabase as any)
      .from("pnl_dashboards")
      .select(DASHBOARD_COLUMNS)
      .eq("organization_id", organizationId)
      .order("name", { ascending: true })
      .then(({ data, error: readError }: { data: RawDashboardRow[] | null; error: { message: string } | null }) => {
        if (cancelled) return;
        if (readError) {
          setError(readError.message);
          setDashboards([]);
        } else {
          setError(null);
          setDashboards((data ?? []).map(decodeDashboard));
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, refreshToken]);

  const refresh = useCallback(() => {
    setLoading(true);
    setRefreshToken((t) => t + 1);
  }, []);

  const createDashboard = useCallback(
    async (name: string, widgets: PnlDashboardWidgetSeed[]): Promise<PnlDashboardRow | null> => {
      if (!organizationId || !userId) {
        addToast({ type: "error", title: "Not signed in to an organisation", message: "Sign in again and retry." });
        return null;
      }

      const { data, error: insertError } = await (supabase as any)
        .from("pnl_dashboards")
        .insert({ organization_id: organizationId, owner_id: userId, name: name.trim() })
        .select(DASHBOARD_COLUMNS)
        .single();

      if (insertError || !data) {
        addToast({
          type: "error",
          title: "Could not create the dashboard",
          message: insertError?.message ?? "The database returned no row.",
        });
        return null;
      }

      const created = decodeDashboard(data as RawDashboardRow);

      if (widgets.length > 0) {
        const { error: widgetError } = await (supabase as any).from("pnl_dashboard_widgets").insert(
          widgets.map((w) => ({
            dashboard_id: created.id,
            position: w.position,
            config: w.config,
            layout: w.layout,
          })),
        );
        if (widgetError) {
          // The dashboard exists but is empty. Say so plainly rather than
          // deleting it behind the user's back: an empty dashboard they can
          // add to beats a create that silently vanished.
          addToast({
            type: "warning",
            title: `"${created.name}" was created without its widgets`,
            message: widgetError.message,
          });
        }
      }

      setDashboards((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      return created;
    },
    [organizationId, userId, addToast],
  );

  const renameDashboard = useCallback(
    async (id: string, name: string): Promise<boolean> => {
      const trimmed = name.trim();
      const { data, error: updateError } = await (supabase as any)
        .from("pnl_dashboards")
        .update({ name: trimmed, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select("id");

      if (updateError) {
        addToast({ type: "error", title: "Could not rename the dashboard", message: updateError.message });
        return false;
      }
      // An UPDATE that matches zero rows returns no error through
      // supabase-js, which is how a project rate edit once "saved" and
      // reverted. Selecting the ids back is what turns that into a message.
      if (!Array.isArray(data) || data.length === 0) {
        addToast({
          type: "error",
          title: "Rename was refused",
          message: "You can only rename a dashboard you own, or a shared one as an organisation admin.",
        });
        return false;
      }

      setDashboards((prev) =>
        prev.map((d) => (d.id === id ? { ...d, name: trimmed } : d)).sort((a, b) => a.name.localeCompare(b.name)),
      );
      return true;
    },
    [addToast],
  );

  const setDashboardShared = useCallback(
    async (id: string, isShared: boolean): Promise<boolean> => {
      const { data, error: updateError } = await (supabase as any)
        .from("pnl_dashboards")
        .update({ is_shared: isShared, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select("id");

      if (updateError) {
        addToast({ type: "error", title: "Could not change sharing", message: updateError.message });
        return false;
      }
      if (!Array.isArray(data) || data.length === 0) {
        addToast({
          type: "error",
          title: "Sharing change was refused",
          message: "Only the owner or an organisation admin can share this dashboard.",
        });
        return false;
      }

      setDashboards((prev) => prev.map((d) => (d.id === id ? { ...d, isShared } : d)));
      return true;
    },
    [addToast],
  );

  const setDatePresets = useCallback(
    async (id: string, presets: PnlCustomDatePreset[]): Promise<boolean> => {
      const { data, error: updateError } = await (supabase as any)
        .from("pnl_dashboards")
        .update({ date_presets: presets, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select("id");

      if (updateError) {
        addToast({ type: "error", title: "Could not save the date preset", message: updateError.message });
        return false;
      }
      if (!Array.isArray(data) || data.length === 0) {
        addToast({
          type: "error",
          title: "Date preset was refused",
          message: "Only the owner or an organisation admin can edit this dashboard.",
        });
        return false;
      }

      setDashboards((prev) => prev.map((d) => (d.id === id ? { ...d, datePresets: presets } : d)));
      return true;
    },
    [addToast],
  );

  const duplicateDashboard = useCallback(
    async (id: string, name: string): Promise<PnlDashboardRow | null> => {
      const source = dashboards.find((d) => d.id === id);
      const { data, error: readError } = await (supabase as any)
        .from("pnl_dashboard_widgets")
        .select("position, config, layout")
        .eq("dashboard_id", id)
        .order("position", { ascending: true });

      if (readError) {
        addToast({ type: "error", title: "Could not read the widgets to copy", message: readError.message });
        return null;
      }

      // `config.widgetId` is deliberately carried over unchanged. It is a
      // config field, unique within a dashboard rather than globally, and the
      // renderer uses it to keep the phase 1 widgets' own controls (the
      // scatter axis toggle, the trend project selector, the table's column
      // and export controls). Regenerating it would make a copy of the
      // default dashboard look unlike the thing it copied, which is exactly
      // what plan decision D11 rules out.
      const widgets: PnlDashboardWidgetSeed[] = ((data ?? []) as { position: number; config: PnlWidgetConfig; layout: PnlWidgetLayout }[]).map(
        (row, index) => ({ config: row.config, layout: row.layout, position: index }),
      );

      const created = await createDashboard(name, widgets);
      if (created && source && source.datePresets.length > 0) {
        await setDatePresets(created.id, source.datePresets);
      }
      return created;
    },
    [dashboards, createDashboard, setDatePresets, addToast],
  );

  const deleteDashboard = useCallback(
    async (id: string): Promise<boolean> => {
      const { data, error: deleteError } = await (supabase as any)
        .from("pnl_dashboards")
        .delete()
        .eq("id", id)
        .select("id");

      if (deleteError) {
        addToast({ type: "error", title: "Could not delete the dashboard", message: deleteError.message });
        return false;
      }
      if (!Array.isArray(data) || data.length === 0) {
        addToast({
          type: "error",
          title: "Delete was refused",
          message: "Only the owner or an organisation admin can delete this dashboard.",
        });
        return false;
      }

      setDashboards((prev) => prev.filter((d) => d.id !== id));
      return true;
    },
    [addToast],
  );

  return {
    dashboards: organizationId ? dashboards : [],
    loading: organizationId ? loading : false,
    error,
    refresh,
    createDashboard,
    renameDashboard,
    duplicateDashboard,
    setDashboardShared,
    setDatePresets,
    deleteDashboard,
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */
